import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { useFeatureFlag } from '@fs/zion-flags'
import { useAdobeTarget } from '@fs/zion-adobe-target'
import { trackEvent } from '@fs/zion-analytics'
import { buildLangUrl } from '@fs/zion-router'
import useUserSegment from '../../hooks/useUserSegment'
import useCountry from '../../hooks/useCountry'
import useAreaDefinition from '../../hooks/useAreaDefinition'
import isAdobeTargetSkipped from '../../utils/isAdobeTargetSkipped'
import LoadingSpinner from '../LoadingSpinner'

export const TREE_BUILDER_AB_TEST_FLAG = 'homelands_homePortal_treeBuilderAbTest'

// Adobe Target mbox (Location) this test listens on. Naming convention: [team]_[page].
export const TREE_BUILDER_AB_MBOX = 'homelands_tree_builder_redirect'

// Adobe Target experience ids for the two arms — must match the experiences in the Target activity.
export const REDIRECT_EXPERIENCE = 'HomelandsTreeBuilderRedirect'
export const CONTROL_EXPERIENCE = 'HomelandsTreeBuilderControl'

export const TREE_BUILDER_URL = '/tree/guided/connect-to-tree'
export const REDIRECT_EVENT_NAME = 'home_portal_tree_builder_ab_test_redirect'

const TEST_COUNTRIES = new Set(['AU', 'NZ', 'PH'])

// "Logging in for the first, second or third time" is what the user segment reports as loginLevel NEW.
const TEST_LOGIN_LEVEL = 'NEW'
const TEST_TREE_LEVELS = new Set(['EMPTY', 'PARTIAL'])

// If Adobe Target never assigns an experience (e.g. at.js is blocked by an extension) the offer would
// never resolve and we would hold the portal forever, so fall back to the control arm after this
// window. It is generous enough that a real (if slow) offer — which can arrive even after Adobe
// Target's own ~3s load timeout — still wins the race, so this only trips on genuine failures.
// This is now only a last resort: an offer that resolves or fails settles us immediately (see below).
export const OFFER_FALLBACK_MS = 5000

// Module scope keeps the reference stable: `useAdobeTarget` re-registers the mbox whenever the config
// identity changes.
const targetConfig = {
  location: TREE_BUILDER_AB_MBOX,
  experienceIds: [REDIRECT_EXPERIENCE, CONTROL_EXPERIENCE],
}

/**
 * A/B test gate for the tree-builder redirect. Wraps the authenticated portal route so the arm is
 * decided after sign-in but before `PortalPage` mounts.
 *
 * Eligible patrons (flag on, in a test market, `loginLevel` NEW, `treeLevel` EMPTY or PARTIAL) are
 * split 50/50 by Adobe Target: the redirect arm is sent to the guided tree builder, the control arm
 * renders the portal. Everyone else renders the portal untouched.
 *
 * The gate is deliberately layered so each tier only does work for patrons who passed the previous
 * one — in particular the mbox is registered only for the eligible cohort, which keeps Adobe Target's
 * measured population equal to the test population.
 */
export default function TreeBuilderAbTest({ children }) {
  const { isOn: abTestOn } = useFeatureFlag(TREE_BUILDER_AB_TEST_FLAG)
  const country = useCountry()
  const { isArabicCountry, isEurope, isSubSaharanAfrica } = useAreaDefinition()

  if (!abTestOn || (!TEST_COUNTRIES.has(country) && !(isArabicCountry || isEurope || isSubSaharanAfrica))) {
    return <>{children}</>
  }

  return <EligibilityGate>{children}</EligibilityGate>
}

TreeBuilderAbTest.propTypes = {
  /** The page to render for the control arm and for every patron outside the test. */
  children: PropTypes.node.isRequired,
}

/** Resolves the user segment, which decides whether this patron is in the test population. */
function EligibilityGate({ children }) {
  const { data: userSegment, isLoading, isError } = useUserSegment()
  const { treeLevel, loginLevel } = userSegment || {}

  // Hold the page until we know, so an eligible patron never sees the portal flash before being
  // redirected. This only delays patrons in a test market, and for them PortalPage requests the same
  // segment anyway (same react-query key), so it costs no extra fetch.
  //
  // `isLoading` is not sufficient on its own. `useUserSegment` calls `useEffectiveUser`, which is
  // per-call-site `useReducer` state rather than shared context, so on our first render its cisId is
  // still undefined — and `getUserSegment` short-circuits to `undefined` without making a request.
  // react-query v3 records that as a *successful* query with no data (verified: status success,
  // isLoading false, isError false), so reading it as "loaded, not eligible" would decide the arm
  // before the segment was ever fetched. Wait for real data or a genuine error instead; once cisId
  // lands the query re-keys and fetches for real.
  if (isLoading || (!userSegment && !isError)) {
    return <LoadingSpinner />
  }

  // Fail open: if the segment request failed we can't tell whether this patron is eligible, and
  // showing the portal is the safe outcome.
  const isEligible = !isError && loginLevel === TEST_LOGIN_LEVEL && TEST_TREE_LEVELS.has(treeLevel)

  if (!isEligible) {
    return <>{children}</>
  }

  return <AssignmentGate>{children}</AssignmentGate>
}

EligibilityGate.propTypes = {
  children: PropTypes.node.isRequired,
}

/** Registers the mbox and renders the arm Adobe Target assigned. Only mounts for eligible patrons. */
function AssignmentGate({ children }) {
  if (isAdobeTargetSkipped()) {
    return <>{children}</>
  }

  return <ActiveAssignmentGate>{children}</ActiveAssignmentGate>
}

AssignmentGate.propTypes = {
  children: PropTypes.node.isRequired,
}

/** Registers the mbox only for clients that can participate in Adobe Target. */
function ActiveAssignmentGate({ children }) {
  const { data, error } = useAdobeTarget(targetConfig)
  const experienceId = data?.experienceId

  // Safety net for eligible patrons whose offer never resolves (e.g. at.js blocked by an extension).
  const [offerFallbackElapsed, setOfferFallbackElapsed] = useState(false)
  useEffect(() => {
    const timeoutId = setTimeout(() => setOfferFallbackElapsed(true), OFFER_FALLBACK_MS)
    return () => clearTimeout(timeoutId)
  }, [])

  if (experienceId === REDIRECT_EXPERIENCE) {
    return <TreeBuilderRedirect />
  }

  // Commit to the control arm only once Adobe Target is definitively done for us: it assigned an
  // experience (control), our offer request failed, or the fallback timer elapsed without an
  // assignment. Until then keep waiting — the shared `loading`
  // flag is not a reliable "done" signal, because it is app-wide and a co-tenant activity can flip it
  // false while our own offer is still in flight. Committing on that would show the portal to a patron
  // who is about to be redirected.
  //
  // `error` *is* a definitive answer, and the common one: when no activity serves our mbox (not yet
  // created, paused, or the patron is outside its audience) Target returns an empty offer list and the
  // provider rejects for our location, so without this every such patron would stall on the spinner
  // for the full OFFER_FALLBACK_MS. `useAdobeTarget` folds the global load-timeout error in with our
  // location's, and we can't tell them apart through its API — so a slow at.js that loads after the
  // provider's ~3s timeout also settles us to control here. That leaks a few would-be redirects into
  // the control arm, the same class of leak we already accept for the fallback timer.
  const targetSettled = Boolean(experienceId) || Boolean(error) || offerFallbackElapsed

  if (!targetSettled) {
    return <LoadingSpinner />
  }

  return <>{children}</>
}

ActiveAssignmentGate.propTypes = {
  children: PropTypes.node.isRequired,
}

/** Sends the patron to the guided tree builder. Renders a spinner while the navigation is in flight. */
function TreeBuilderRedirect() {
  const hasRedirectedRef = useRef(false)

  useEffect(() => {
    if (hasRedirectedRef.current) {
      return
    }

    hasRedirectedRef.current = true
    trackEvent({ event_name: REDIRECT_EVENT_NAME })
    window.location.assign(buildLangUrl(TREE_BUILDER_URL))
  }, [])

  return <LoadingSpinner />
}
